import React, { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import FieldError from '@components/shared/form/FieldError'
import { resizeSelect } from '@utils/helpers/select.helper'

interface Props extends React.SelectHTMLAttributes<HTMLSelectElement> {
  name?: string
  error?: string
  label?: string
  variant?: 'sm' | 'base'
  borderless?: boolean
  autoWidth?: boolean
  solidBackground?: boolean
  selectClassName?: string
}

const Dropdown: React.FC<Props> = ({
  variant = 'base',
  name,
  label,
  error,
  className,
  children,
  borderless,
  autoWidth,
  solidBackground,
  selectClassName,
  onChange,
  ...props
}: Props) => {
  const [focused, setFocused] = useState(false)
  const ref = useRef<HTMLSelectElement>(null)

  const doOnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (typeof onChange === 'function') onChange(event)
    if (autoWidth && ref.current) {
      resizeSelect(ref.current)
    }
  }

  useEffect(() => {
    if (autoWidth && ref.current) {
      resizeSelect(ref.current)
    }
  }, [autoWidth])

  return (
    <div className={classNames('flex flex-col', className)}>
      {label && (
        <label className="text-md mb-2 w-full text-black dark:text-gray" htmlFor={props.id ?? name}>
          {label}
        </label>
      )}
      <div
        className={classNames(
          'relative h-full rounded-sm select w-full outline-none flex items-center text-xl',
          borderless
            ? 'border-none hover:bg-gray-highlight dark:hover:bg-gray-highlight-dm focus:bg-gray-highlight dark:focus:bg-gray-highlight-dm mix-blend-multiply dark:mix-blend-normal'
            : 'border mb-2',
          error ? 'border-orange' : 'border-gray',
          {
            'border-orange': error && !focused,
            'border-gray dark:border-gray-border-dm': !error && !focused,
            'border-gray-text': focused,
          }
        )}>
        <select
          ref={ref}
          name={name}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className={classNames(
            'appearance-none text-md w-full outline-none cursor-pointer p-8 ellipsify text-black dark:text-gray',
            {
              'bg-transparent': !solidBackground,
              'bg-white dark:bg-black-bg2': solidBackground,
              'pl-4 py-3': variant === 'base',
              'pl-2.5 py-1.5': variant === 'sm',
            },
            selectClassName
          )}
          onChange={doOnChange}
          {...props}>
          {children}
        </select>
        <i className="pr-1 nf-icon nf-chevron-down absolute right-0 pointer-events-none text-black dark:text-gray" />
      </div>
      <FieldError error={error} />
    </div>
  )
}

Dropdown.defaultProps = {
  variant: 'base',
}

export default Dropdown
